/* eslint-disable max-lines */
import { Box, Stack, Typography } from '@mui/material';
import cx from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Calendar, dayjsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useDispatch, useSelector } from 'react-redux';

import CalendarColumnHeader from './CalendarColumnHeader';
import { customDayLayoutAlgorithm } from './customDayLayoutAlgorithm';
import { pushSnackbar } from 'FRONTEND/actions/app';
import AppointmentList from 'FRONTEND/components/Calendar/AppointmentList/AppointmentList';
import CalendarError from 'FRONTEND/components/Calendar/CalendarError';
import CalendarPopover from 'FRONTEND/components/Calendar/CalendarPopover';
import { CONFIRM_REASON } from 'FRONTEND/components/Calendar/CalendarPopover/closeReasons';
import CalendarToolbar from 'FRONTEND/components/Calendar/CalendarToolbar';
import {
  getCalendarMessages,
  getCalendarViews,
  isScheduleAvailable,
  setEventStyle,
} from 'FRONTEND/components/Calendar/utils';
import {
  cancelRequest as deleteAppointmentRequest,
  updateRequest as updateAppointmentRequest,
} from 'FRONTEND/reducers/appointmentSlice';
import {
  createDraft as createAvailabilityDraft,
  createRequest as createAvailabilityRequest,
  deleteDraft as deleteAvailabilityDraft,
  listRequest as listAvailabilitiesRequest,
  resetErrors as resetAvailabilityErrors,
  selectActorFilteredAvailabilities,
  selectAvailabilityById,
  selectFilteredAvailabilities,
  stopEditing as stopEditingAvailability,
  updateRequest as updateAvailabilityRequest,
} from 'FRONTEND/reducers/availabilitySlice';
import {
  goToActorCalendar,
  pickEvent,
  selectAppointmentStatusFilterList,
  selectCurrentDay,
  selectEventKindFilterList,
  updateCurrentDay,
} from 'FRONTEND/reducers/calendarSlice';
import { selectAppoinmentAndAvailabilityLocations } from 'FRONTEND/reducers/locationSlice';
import { listResources } from 'FRONTEND/reducers/resourceSlice';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import {
  selectExpertAppointmentById,
  selectTypeFilteredAppointmentsForActor,
} from 'FRONTEND/selectors/appointmentSelectors';
import { selectCalendarActor } from 'FRONTEND/selectors/calendarSelectors';
import {
  APPOINTMENT_KIND,
  APPOINTMENT_STATUS_CANCELLED,
  AVAILABILITY_KIND, DELETE_ERROR, DRAFT_AVAILABILITY_ID, LIST_ERROR, UPDATE_ERROR,
} from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { CALENDAR_DAY_END, CALENDAR_DAY_START, MINUTES_PER_STEP, STEPS_PER_HOUR, CALENDAR_VIEWS } from 'LEGACY/constants';

import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const DragAndDropCalendar = withDragAndDrop(Calendar);
const localizer = dayjsLocalizer(dayjs);
const LOADING_TRIGGER_ACTION_CREATOR_LIST = [
  updateAppointmentRequest,
  deleteAppointmentRequest,
  updateAvailabilityRequest,
  createAvailabilityRequest,
  listAvailabilitiesRequest,
];

const CustomEvent = ({ event, title }) => {
  const isSingleActor = useSelector((state) => Boolean(state.calendar.actorId));
  const expertCheckedList = useSelector((state) => state.expert.checkedList);
  const colorIndex = isSingleActor ? null : expertCheckedList.indexOf(event.expertId || event.actorId);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    setEventStyle(contentRef.current, event, colorIndex);
  }, [contentRef.current, event.status, event.startDate, event.endDate, colorIndex]);

  return (
    <div ref={contentRef} data-event-color-index={colorIndex} id={event.appointmentId || event.availabilityId} style={{ width: '100%' }}>
      {title}
    </div>
  );
};

const getTooltip = (event) => {
  const locationInfo = event?.location?.id ? `/ ${i18n._('Location')}: ${event.location.display}` : '';
  return (`${event.title} ${locationInfo}`);
};

const WeeklyCalendar = ({
  rtcServerUrl,
}) => {
  const dispatch = useDispatch();

  // Custom touch event management
  const [didTouchStart, setDidTouchStart] = useState(true);
  const handleTouchStart = (event) => {
    // Prevent all touch events related side effects aside from scrolling.
    event.stopImmediatePropagation();

    // targetTouches is an array (Each touch individually for multi-finger touch support)
    const firstTouch = event.targetTouches[0];
    if (!firstTouch) {
      return;
    }

    // Dispatch a touchstart event on the container in order to still enable scroll
    const eventsContainer = document.getElementsByClassName('rbc-time-view')?.[0];
    if (eventsContainer) {
      const touchObj = new Touch({
        identifier: Date.now(),
        target: eventsContainer,
        clientX: firstTouch.clientX,
        clientY: firstTouch.clientY,
        // pageX/pageY are required by react-big-calendar's Selection to compute the correct time slot bounding box
        pageX: firstTouch.pageX,
        pageY: firstTouch.pageY,
        radiusX: firstTouch.radiusX,
        radiusY: firstTouch.radiusY,
        rotationAngle: firstTouch.rotationAngle,
        force: firstTouch.force,
      });
      const touchEvent = new TouchEvent('touchstart', {
        cancelable: true,
        bubbles: true,
        touches: [touchObj],
        targetTouches: [],
        changedTouches: [touchObj],
        shiftKey: true,
      });
      eventsContainer.dispatchEvent(touchEvent);

      // Need to store this in state, because we need didTouchStart to be updated before onSelectSlot callback
      setDidTouchStart(true);
    }
  };
  useEffect(() => {
    const calendarContentElement = document.getElementsByClassName('rbc-time-content')?.[0];
    if (calendarContentElement) {
      calendarContentElement.addEventListener('touchstart', handleTouchStart);
    }
    return () => {
      if (calendarContentElement) {
        calendarContentElement.removeEventListener('touchstart', handleTouchStart);
      }
    };
  }, []);

  // Calendar events management
  const eventKindFilterList = useSelector(selectEventKindFilterList);
  const calendarActor = useSelector(selectCalendarActor);
  const availabilities = useSelector((state) => calendarActor ? selectActorFilteredAvailabilities(state, calendarActor?.id) : selectFilteredAvailabilities(state));
  const appointments = useSelector(state => selectTypeFilteredAppointmentsForActor(state, calendarActor));
  const statusFilterList = useSelector(selectAppointmentStatusFilterList);
  const currentDay = useSelector(selectCurrentDay);
  const hasAvailabilityCreateRight = useSelector(state => selectHasRight(state, RIGHTS.AVAILABILITIES_CREATE));
  const hasAvailabilityUpdateRight = useSelector(state => selectHasRight(state, RIGHTS.AVAILABILITIES_UPDATE));
  const hasServicesRights = useSelector(state => selectHasRight(state, RIGHTS.SERVICES_LIST));
  const hasManagementRight = useSelector((state) => selectHasRight(state, RIGHTS.CALENDAR_MANAGEMENT));
  const currentUserId = useSelector((state) => state.user.id);

  const shouldAllowResizeOrDrag = (event) => {
    return hasAvailabilityUpdateRight && calendarActor?.id && !event.appointmentId;
  };
  // Location filter management
  const appointmentLocation = useSelector(selectAppoinmentAndAvailabilityLocations);
  const checkedLocationsList = useSelector((state) => state.location.checkedLocationsList);

  useEffect(() => {
    if (hasServicesRights) {
      dispatch(listResources());
    }
    // If for whatever reason user lost multi-calendar management rights, ensure the calendar being managed is currentUser.
    if (!hasManagementRight) {
      dispatch(goToActorCalendar({ actorId: currentUserId, actorType: 'expert' }));
    }
  }, []);

  // Event list management
  // Computed from states, no need of useEffect
  let events = [];
  let shouldAddAvailabilities = false; // Ensure we only concatenate availabilities once.
  if (eventKindFilterList.includes(AVAILABILITY_KIND)) {
    shouldAddAvailabilities = true;
  }
  if (eventKindFilterList.includes(APPOINTMENT_KIND)) {
    const filteredAppointments = appointments.filter((appointment) => statusFilterList.includes(appointment.status));
    events = events.concat(filteredAppointments);
  }
  if (appointmentLocation.length > 0) {
    events = events.filter((event) => checkedLocationsList.includes(event?.location?.id) || !event?.location);
    shouldAddAvailabilities = true;
  }
  if (shouldAddAvailabilities) {
    const locationFilteredAvailabilities = availabilities.filter((availability) => {
      // only filter if availability has a location associated and is not a draft
      if (availability?.location?.id && availability.availabilityId !== DRAFT_AVAILABILITY_ID) {
        return checkedLocationsList.includes(availability?.location?.id);
      } else {
        return availability;
      }
    });
    events = events.concat(locationFilteredAvailabilities);
  }

  const isAvailabilityResizeValid = (start, end, event) => {
    const scheduledAppointments = appointments.filter((apt) => dayjs(event.start).isSameOrBefore(apt.startDate) && dayjs(event.end).isSameOrAfter(apt.endDate));
    return scheduledAppointments.every((apt) => dayjs(start).isSameOrBefore(apt.startDate) && dayjs(end).isSameOrAfter(apt.endDate));
  };

  const handleAvailabilityUpdate = ({ start, end, event }) => {
    // Preventing updating availabilities so that their end date exceeds D+1 00:00
    if (dayjs(end).date() > dayjs(start).date()) {
      return;
    }
    // MUI TimePicker doesn't validate 23:59 as long as we want it to have a 5-minute step and to do date validation.
    // So we round 23:59 to 23:55 to avoid any issues.
    if (dayjs(end).hour() === 23 && dayjs(end).minute() === 59) {
      end = dayjs(end).minute(55).second(0).toDate();
    }
    if (!event.appointmentId && isAvailabilityResizeValid(start, end, event) && event.availabilityId) {
      if (isScheduleAvailable({
        availabilities: availabilities.filter(({ availabilityId }) => availabilityId !== event.availabilityId),
        startDate: start.toJSON(),
        endDate: end.toJSON(),
      })) {
        dispatch(updateAvailabilityRequest({
          availabilityId: event.availabilityId,
          actorId: event.actorId,
          actorType: event.actorType,
          startDate: start.toJSON(),
          endDate: end.toJSON(),
          locationId: event.location?.id,
        }));
      } else {
        dispatch(pushSnackbar({
          message: i18n._('Availability cannot be changed or moved there because it would overlap with another one.'),
          variant: 'warning',
        }));
      }
    }
  };

  const handleAvailabilityCreate = ({ start, end }) => {
    // MUI TimePicker doesn't validate 23:59 as long as we want it to have a 5-minute step and to do date validation.
    // So we round 23:59 to 23:55 to avoid any issues.
    if (dayjs(end).hour() === 23 && dayjs(end).minute() === 59) {
      end = dayjs(end).minute(55).second(0).toDate();
    }
    dispatch(createAvailabilityDraft({
      actorId: calendarActor?.id,
      actorType: calendarActor?.actorType,
      startDate: start.toJSON(),
      endDate: end.toJSON(),
      title: i18n._('Availability'),
    }));
    dispatch(pickEvent({ eventId: DRAFT_AVAILABILITY_ID, actorId: calendarActor?.id }));
  };

  // Dialog management
  /**
   * @param {'confirm'|'delete'|'cancel'} reason
   */
  const handleDialogClose = (reason) => {
    // The draft is deleted no matter the close reason
    if (selectedEvent.availabilityId === DRAFT_AVAILABILITY_ID) {
      dispatch(deleteAvailabilityDraft());
    }
    // If an update request was sent, the edit mode will be left after the result. If not, we can leave it now.
    if (selectedEvent.isEditing && reason !== CONFIRM_REASON) {
      dispatch(stopEditingAvailability({ id: selectedEvent.availabilityId, shouldRestore: true }));
    }
    dispatch(pickEvent({ eventId: null, actorId: null }));
  };

  // Ensures translation keys exist and are detected in calendar.
  const appointmentTypeMap = {
    teleconsultation: i18n._('Teleconsultation'),
    telestaff: i18n._('Telestaff'),
    other_appointment: i18n._('Other (in-person, etc.)'),
  };

  const getTitleAccessor = (event) => {
    const isCancelled = event?.status === APPOINTMENT_STATUS_CANCELLED;
    return (
      <Stack width='100%' overflow='hidden'>
        <Typography
          variant='body2'
          noWrap
          textOverflow='ellipsis'
          color='black'
          sx={{
            textDecoration: isCancelled ? 'line-through' : 'none',
            fontSize: 'inherit',
          }}
        >
          {event?.appointmentType ? appointmentTypeMap[event.appointmentType] : ''}
        </Typography>
      </Stack>
    );
  };

  const selectedEvent = useSelector((state) => selectAvailabilityById(state, state.calendar.selectedEventId) || selectExpertAppointmentById(state, state.calendar.selectedEventId, state.calendar.selectedEventActorId));
  const [selectedEventEl, setSelectedEventEl] = useState(null);
  useEffect(() => {
    setSelectedEventEl(selectedEvent && document.getElementById(selectedEvent.appointmentId || selectedEvent.availabilityId));
  }, [events, selectedEvent]);

  // Layout management
  const isLoading = useSelector((state) => LOADING_TRIGGER_ACTION_CREATOR_LIST.some((actionCreator) => state.app.requestCounter[actionCreator.toString()] > 0));
  const isSmall = useSelector((state) => state.app.isSmall);
  const isCalendarEditable = hasAvailabilityCreateRight && Boolean(calendarActor) && eventKindFilterList.includes(AVAILABILITY_KIND);
  const classes = cx('calendar', {
    calendar_small: isSmall,
    calendar_editable: isCalendarEditable,
  });
  const shouldDisplayAvailabilityDeletionError = useSelector((state) => [DELETE_ERROR, UPDATE_ERROR].includes(state.availability.errorType) && state.availability.error === '400');
  const shouldDisplayRightError = useSelector((state) => state.availability.errorType === LIST_ERROR && state.availability.error === '400');

  // Extract toolbar props and keep track of them.
  const [toolbarProps, setToolbarProps] = useState(null); // custom props extracted from react-big-calendar toolbar component
  const calendarView = useSelector((state) => state.calendar.view);

  return (
    <div className={classes}>
      <CalendarPopover
        rtcServerUrl={rtcServerUrl}
        calendarEvent={selectedEvent}
        anchorEl={selectedEventEl}
        onClose={handleDialogClose}
      />
      {toolbarProps && <CalendarToolbar isLoading={isLoading} {...toolbarProps} />}
      {calendarView === CALENDAR_VIEWS.PLANNING && <AppointmentList rtcServerUrl={rtcServerUrl} />}
      <Box
        // Ensure we still render the calendar in planning view (in order to get toolbarProps), but hide it.
        sx={{ display: calendarView === CALENDAR_VIEWS.PLANNING ? 'none' : 'block', minHeight: '0' }}
      >
        <DragAndDropCalendar
          components={{
            event: CustomEvent,
            header: CalendarColumnHeader,
            toolbar: (props) => {
              // WORKAROUND: Extract the props but don't render anything here
              // Select fields don't behave properly (state management) when directly rendered in a custom toolbar component
              // This is a known issue with react-big-calendar where stateful components lose state due to re-rendering cycles
              // Only update if props have actually changed to avoid infinite render loop
              if (!toolbarProps || toolbarProps.view !== props.view || toolbarProps.date !== props.date) {
                setToolbarProps(props);
              }
              return null;
            },
          }}
          resizable={isCalendarEditable}
          draggable={isCalendarEditable}
          selectable={isCalendarEditable}
          localizer={localizer}
          events={events}
          defaultView={Views.WEEK}
          date={currentDay}
          onEventResize={handleAvailabilityUpdate}
          onEventDrop={handleAvailabilityUpdate}
          onSelectSlot={handleAvailabilityCreate}
          onSelectEvent={(data) => {
            if (!didTouchStart) {
              // TODO: do we need to handle the case where there are no actorId?
              dispatch(pickEvent({ eventId: data.appointmentId || data.availabilityId, actorId: data.actorId || data.expertId }));
            } else {
              setDidTouchStart(false);
            }
          }}
          onNavigate={(date) => dispatch(updateCurrentDay({ currentDay: date.toISOString() }))}
          views={getCalendarViews(isSmall)}
          step={MINUTES_PER_STEP}
          timeslots={STEPS_PER_HOUR}
          min={CALENDAR_DAY_START}
          max={CALENDAR_DAY_END}
          messages={getCalendarMessages()}
          scrollToTime={new Date(1970, 1, 1, 8)}
          showMultiDayTimes
          resizableAccessor={shouldAllowResizeOrDrag}
          draggableAccessor={shouldAllowResizeOrDrag}
          dayLayoutAlgorithm={customDayLayoutAlgorithm}
          startAccessor={(event) => new Date(event.startDate)}
          endAccessor={(event) => {
            const endDate = new Date(event.endDate);
            const midnightDate = new Date(event.endDate);
            midnightDate.setHours(0, 0, 0, 0);
            // Preventing react-big-calendar incorrect duplicate when event ends at midnight
            if (endDate.getTime() === midnightDate.getTime()) {
              const adjustedEndDate = new Date(endDate);
              adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
              adjustedEndDate.setHours(23, 59, 59, 999);
              return adjustedEndDate;
            }
            return endDate;
          }}
          titleAccessor={getTitleAccessor}
          tooltipAccessor={getTooltip}
        />
        <CalendarError
          open={shouldDisplayAvailabilityDeletionError}
          onClose={() => dispatch(resetAvailabilityErrors())}
          title={i18n._('Availability')}
        />
        <CalendarError
          open={shouldDisplayRightError}
          onClose={() => dispatch(resetAvailabilityErrors())}
          title={i18n._('Not authorized')}
        >
          {i18n._('A user whose calendar is selected does not have a profile that allows delegated access. Please contact a user manager.')}
        </CalendarError>
      </Box>
    </div>
  );
};

export default WeeklyCalendar;
